import {
  type AppModalEntry,
  AppModalKey,
  type AppModalOpenArgs,
  type AppModalProps,
} from "./AppModals.types";
import { useAppModalsContext } from "./AppModalsProvider";

/** One app-wide modal: whether it is open, with which props, and how to open or close it. */
const useAppModal = <TModalKey extends AppModalKey>(modal: TModalKey) => {
  const context = useAppModalsContext();
  // The state is keyed loosely; narrow to this modal's slot.
  const entry = context.modals[modal] as AppModalEntry<TModalKey> | undefined;

  // Takes props, or an updater to change an already open modal's props. The rest
  // tuple is what lets modals with only optional props skip the argument.
  const openModal = (...[nextProps]: AppModalOpenArgs<TModalKey>) => {
    // Only reachable without props when they are all optional, so `{}` is valid.
    context.openModal(modal, nextProps ?? ({} as AppModalProps[TModalKey]));
  };

  // Keeps the modal mounted with its props, so it can animate out.
  const closeModal = () => {
    context.closeModal(modal);
  };

  // For a page that is going away: unmounts the modal at once, without animating it
  // out over the next page, and drops its props and the callbacks in them.
  const removeModal = () => {
    context.removeModal(modal);
  };

  return {
    closeModal,
    isModalOpen: entry?.isOpen ?? false,
    openModal,
    /** Kept while the modal closes; read `isModalOpen` for whether it is open. */
    modalProps: entry?.props,
    removeModal,
  };
};

const usePhoneVerificationModal = () =>
  useAppModal(AppModalKey.PhoneVerificationModal);

const useProfileModal = () => useAppModal(AppModalKey.ProfileModal);

const useShareModal = () => useAppModal(AppModalKey.ShareModal);

const useVerifyEmailModal = () => useAppModal(AppModalKey.VerifyEmailModal);

export {
  useAppModal,
  usePhoneVerificationModal,
  useProfileModal,
  useShareModal,
  useVerifyEmailModal,
};
